import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Calendar, Rocket, Users } from "lucide-react";

function Counter({ to, suffix = "", duration = 1.6 }) {
    const [val, setVal] = useState(0);
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });

    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        let raf;
        const step = (t) => {
            const p = Math.min(1, (t - start) / (duration * 1000));
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(eased * to));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [inView, to, duration]);

    return (
        <span ref={ref} data-testid="counter-value">
            {val}
            {suffix}
        </span>
    );
}

const STATS = [
    {
        icon: Calendar,
        value: 2014,
        label: "Started as Digital 2wist Inc.",
        suffix: "",
        testid: "stat-2014",
    },
    {
        icon: Rocket,
        value: 2017,
        label: "Evolved into Amplified eSolutions Inc.",
        suffix: "",
        testid: "stat-2017",
    },
    {
        icon: Users,
        value: 26,
        label: "Combined team experience",
        suffix: "+ yrs",
        testid: "stat-experience",
    },
];

export default function Stats() {
    return (
        <section
            id="stats"
            data-testid="stats-section"
            className="relative py-16 md:py-24 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {STATS.map((s, i) => (
                        <motion.div
                            key={s.testid}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.7,
                                delay: i * 0.1,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={s.testid}
                            className="group relative bg-white border border-black/5 rounded-3xl px-6 py-7 md:px-8 md:py-9 shadow-ae-soft hover:shadow-ae-lift hover:border-ae-orange/40 hover:-translate-y-1 transition-all duration-500"
                        >
                            <div className="flex items-start gap-5">
                                <div className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-ae-orange/10 text-ae-orange flex items-center justify-center group-hover:bg-ae-orange group-hover:text-white transition-colors duration-500">
                                    <s.icon className="h-6 w-6" />
                                </div>
                                <div>
                                    <div className="font-display font-extrabold text-3xl md:text-5xl text-ae-ink tracking-tight leading-none">
                                        <Counter to={s.value} suffix={s.suffix} />
                                    </div>
                                    <div className="mt-2 text-sm md:text-base text-ae-gray-700 max-w-[18rem]">
                                        {s.label}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
